/**
 * Origem no painel da conversa: sempre visível.
 * Edita a origem do CONTATO (não da negociação).
 * Anúncios pagos (Meta / Instagram ads / Google) são somente leitura.
 */
import React, { useId, useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Flag, Layers, FileText, Search } from 'lucide-react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { EditableSelect } from '../../../../components/shared/InlineEditable';
import { Lead, Contact } from '../../../../types';
import { MetaAttribution } from '../../hooks/types';
import { MetaLogoIcon, GoogleAdsLogoIcon } from '../../../../components/icons/CustomIcons';
import { useOrigins } from '../../../settings/origins/hooks/useOrigins';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { useContactOriginAttribution } from '../../hooks/useContactOriginAttribution';
import { adsGroupLabel } from '../../lib/adsOriginPanel';

interface ContactPanelOriginProps {
    isOpen: boolean;
    onToggle: () => void;
    currentLead: Lead | null | undefined;
    metaAttribution?: MetaAttribution;
    remoteJid?: string;
    currentContact?: Contact | null;
    platform?: string | null;
    onUpdateContact?: (field: string, value: unknown) => Promise<void>;
    onLeadUpdate?: (lead: Lead) => Promise<void>;
}

const ADS_BADGE_LABEL = { meta: 'Meta Ads', google: 'Google Ads' } as const;

function AdsOriginBadge({ kind }: { kind: 'meta' | 'google' }) {
    const Icon = kind === 'meta' ? MetaLogoIcon : GoogleAdsLogoIcon;
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-subtle px-2.5 py-0.5 text-caption font-medium text-primary">
            <Icon className="size-3.5" aria-hidden />
            {ADS_BADGE_LABEL[kind]}
        </span>
    );
}

function AdsDetailRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
    return (
        <div className="border-b border-border last:border-0 p-3">
            <div className="flex gap-3">
                <Icon className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
                <div className="min-w-0">
                    <dt className="mb-0.5 text-caption text-muted">{label}</dt>
                    <dd className="text-body-sm font-medium leading-snug text-default text-pretty">{value}</dd>
                </div>
            </div>
        </div>
    );
}

function contactDisplayLabel(
    contactOriginLabel: string | undefined,
    platform?: string | null,
): string {
    if (contactOriginLabel) return contactOriginLabel;
    if (platform === 'instagram') return 'Instagram Direct';
    return 'WhatsApp';
}

export const ContactPanelOrigin: React.FC<ContactPanelOriginProps> = ({
    isOpen,
    onToggle,
    currentLead,
    metaAttribution,
    remoteJid,
    currentContact,
    platform,
    onUpdateContact,
    onLeadUpdate,
}) => {
    const { origins } = useOrigins();
    const { currentOrganization } = useOrganization();
    const {
        kind,
        adsKind,
        isFromAds,
        contactOriginLabel,
        campaign,
        adSet,
        ad,
        keyword,
    } = useContactOriginAttribution({
        isOpen,
        currentLead,
        metaAttribution,
        remoteJid,
        currentContact,
        organizationId: currentOrganization?.id,
        onLeadUpdate,
    });

    const canEdit = !isFromAds && !!onUpdateContact && (!!currentContact || platform !== 'instagram');
    const displayLabel = contactDisplayLabel(contactOriginLabel, platform);

    const originValueId = useMemo(() => {
        if (currentContact?.origin_id) return currentContact.origin_id;
        return origins.find((o) => o.name === displayLabel)?.id ?? '';
    }, [currentContact?.origin_id, origins, displayLabel]);

    const originOptions = useMemo(
        () => origins.map((o) => ({ value: o.id, label: o.name })),
        [origins],
    );

    const adsRows = (
        [
            campaign && { icon: Flag, label: 'Campanha', value: campaign },
            adSet && { icon: Layers, label: adsGroupLabel(kind), value: adSet },
            ad && { icon: FileText, label: 'Anúncio', value: ad },
            keyword && { icon: Search, label: 'Palavra-chave', value: keyword },
        ] as const
    ).filter(Boolean) as Array<{ icon: LucideIcon; label: string; value: string }>;

    const panelId = useId();

    return (
        <section>
            <button
                type="button"
                onClick={onToggle}
                className="flex min-h-11 w-full items-center justify-between px-3 py-2"
                aria-expanded={isOpen}
                aria-controls={panelId}
            >
                <div className="flex items-center gap-2 chat-section-heading">
                    <span>Origem</span>
                    {adsKind ? <AdsOriginBadge kind={adsKind} /> : null}
                </div>
                <AppIcon
                    name="chevron-down"
                    className={`chat-accordion-chevron ${isOpen ? '' : 'chat-accordion-chevron--closed'}`}
                    aria-hidden="true"
                />
            </button>
            {isOpen && (
                <div id={panelId} className="bg-card px-3 pb-3">
                    {isFromAds ? (
                        adsRows.length > 0 ? (
                            <dl className="mt-0 overflow-hidden rounded-lg border border-border bg-card p-0">
                                {adsRows.map((row) => (
                                    <AdsDetailRow key={row.label} icon={row.icon} label={row.label} value={row.value} />
                                ))}
                            </dl>
                        ) : (
                            <p className="rounded-lg border border-border bg-card p-3 text-body-sm text-muted italic">
                                Detalhes da campanha não disponíveis
                            </p>
                        )
                    ) : (
                        <EditableSelect
                            value={originValueId}
                            displayValue={displayLabel}
                            options={originOptions}
                            placeholder="Selecione..."
                            canEdit={canEdit}
                            editTitle="Clique para editar a origem do contato"
                            className="text-body-sm text-muted"
                            onSave={async (originId) => {
                                if (onUpdateContact) {
                                    await onUpdateContact('origin_id', originId || null);
                                }
                            }}
                        />
                    )}
                </div>
            )}
        </section>
    );
};
